import React, { useCallback, useState } from 'react';
import { IonContent, IonIcon } from '@ionic/react';
import { Container } from '@mindly/ui-components';
import { JSX } from '@ionic/core/components';
import { ISubscription } from '../../../redux/subscriptions/types';
import { FullWidthModal } from '../SubscriptionControl.styled';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { closeCircleOutline } from 'ionicons/icons';
import TrashIcon from './SVG/trash.svg?react';
import FirstStep from './Steps/FirstStep';
import ReasonStep from './Steps/ReasonStep';
import LoaderStep from './Steps/LoaderStep';
import FinishStep from './Steps/FinishStep';
import { CancelSubscriptionModalWrapper } from './CancelSubscriptionModal.style';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from 'src/redux/user/selector';
import { cancelSubscription } from 'src/api/subscription';
import { FormattedMessage, useIntl } from 'react-intl';
import { fetchSubscriptions } from '../../../redux/subscriptions/thunk';
import useAgoraRtm from 'src/context/AgoraContext/AgoraContext';
import { SystemMessagesType, UserType } from '../../types';
import { formatSystemMessage } from '../../../context/AgoraContext/formatSystemMessage';
import useLangs from 'src/context/LangsContext';
import { selectRoomDataBySenderId } from '../../../redux/chatRooms/selector';
import EmailSystem from '../../../api/emails';
import HelpButton from '../../../components/HelpButton';
import { SUBSCRIPTION_PAUSE } from '../../../intercom-articles';
import useToast from '../../../context/ToastContext/ToastContext';

type CancelSubscriptionModalProps = {
  onDidDismiss: () => void;
  subscription?: ISubscription | null;
  onPauseSubscription: () => void;
} & JSX.IonModal;

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  subscription,
  onPauseSubscription,
  ...props
}) => {
  const intl = useIntl();
  const { locale } = useLangs();
  const dispatch = useDispatch();

  const { showToast } = useToast();

  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [step, setStep] = useState(1);

  const user = useSelector(userSelector);
  const roomData = useSelector(selectRoomDataBySenderId(subscription?.therapist_id ?? ''));

  const { sendSystemMessage } = useAgoraRtm();

  const onClickNextHandler = useCallback(async () => {
    try {
      if (!subscription?.id)
        throw new Error(
          intl.formatMessage({
            id: 'subscription.error.data_not_loaded_yet',
            defaultMessage: 'Схоже дані не завантажились.',
          })
        );

      setStep(3);

      await cancelSubscription(user?.id, subscription.id);
      await dispatch(fetchSubscriptions(user?.id));

      const therapistFullName = `${subscription.therapist.first_name} ${subscription.therapist.last_name[0]}.`;

      sendSystemMessage({
        user: user as Omit<UserType, 'id'> & { id: string },
        specialistId: subscription.therapist_id,
        message: formatSystemMessage(
          SystemMessagesType.subscription_cancel,
          {
            therapistName: therapistFullName,
            formattedSubscriptionEndDate: new Date(subscription.period_end * 1000).toLocaleString(locale, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
          },
          intl
        ),
        notifyMessage: intl.formatMessage({
          id: 'subscription.notify_message.cancel',
          defaultMessage: 'Призупинив(ла) підписку',
        }),
        roomData,
        locale,
      });
      EmailSystem.sendSubscription('cancelled', subscription.id);

      setStep(4);
    } catch (error: any) {
      setStep(2);
      showToast({ error });
      await props.onDidDismiss();
    }
  }, [subscription, selectedAnswer]);

  const handleCloseModal = useCallback(() => {
    props.onDidDismiss();
    setStep(1);
  }, [props.onDidDismiss]);

  return (
    <FullWidthModal
      {...props}
      onIonModalWillDismiss={() => {
        setSelectedAnswer(null);
      }}
    >
      {step !== 3 && (
        <PageHeader backChevron={step === 1 || step === 2} backChevronAction={handleCloseModal} showAvatar={step !== 4}>
          <IonIcon style={{ fontSize: '27px' }} icon={closeCircleOutline} onClick={handleCloseModal} />
        </PageHeader>
      )}
      <IonContent>
        <Container
          style={{
            paddingTop: 0,
            marginTop: '-15px',
            justifyContent: 'center',
          }}
        >
          <CancelSubscriptionModalWrapper>
            {(step === 1 || step === 2) && <TrashIcon className={'trash-icon'} />}
            {(() => {
              switch (step) {
                case 1:
                  return (
                    <FirstStep
                      handleNextStep={() => setStep(2)}
                      handlePauseSubscription={() => {
                        handleCloseModal();
                        onPauseSubscription();
                      }}
                    />
                  );
                case 2:
                  return (
                    <ReasonStep
                      subscription={subscription}
                      handleNextStep={onClickNextHandler}
                      handleBackStep={() => setStep(1)}
                      handleSetReason={setSelectedAnswer}
                    />
                  );
                case 3:
                  return <LoaderStep />;
                case 4:
                  return <FinishStep subscription={subscription} handleClose={handleCloseModal} />;
              }
            })()}
          </CancelSubscriptionModalWrapper>
          {step === 1 && (
            <HelpButton articleId={SUBSCRIPTION_PAUSE} style={{ position: 'fixed', bottom: '50px' }}>
              <FormattedMessage id={'subscription.cancel.help.btn'} defaultMessage={'Що таке призупинити підписку?'} />
            </HelpButton>
          )}
        </Container>
      </IonContent>
    </FullWidthModal>
  );
};

export default CancelSubscriptionModal;
